/* import React, { useEffect, useState } from "react";
import { Button } from "rizzui/button";
import { Text } from "rizzui/typography";
import useVapi from "../hooks/useVapi";
import { useModal } from "../hooks/useModal";
import { useTestCampaignVariables } from "../hooks/useCampaign";

export default function CampaignTestModal({ campaign_id }) {
  const { closeModal, isOpen } = useModal();
  const [assistant, setAssistant] = useState(null);
  const [shouldStartCall, setShouldStartCall] = useState(false);

  const vapi = useVapi();
  const { data, error, refetch } = useTestCampaignVariables({
    id: campaign_id,
  });

  useEffect(() => {
    isOpen && refetch();
  }, [isOpen]);

  useEffect(() => {
    if (data?.data) {
      setAssistant(data.data);
      setShouldStartCall(true);
    }
  }, [data]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (isOpen && shouldStartCall && assistant) {
      timeout = setTimeout(() => {
        if (isOpen) {
          vapi.startCall(assistant);
        }
      }, 0);
      setShouldStartCall(false);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
      vapi.stopCall();
    };
  }, [isOpen, shouldStartCall, assistant]);

  useEffect(() => {
    if (!isOpen && vapi.isListening) {
      console.log("Stopping call due to modal close");
      vapi.stopCall();
    }

    if (!isOpen) {
      setShouldStartCall(false);
      setAssistant(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (vapi.callEnded) {
      closeModal();
    }
  }, [vapi.callEnded]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div className="border-muted border-[0.5px] h-[266px] w-[312px] bg-background dark:bg-[#2A2C30] p-[32px] rounded-[8px] shadow-lg flex flex-col justify-between items-center">
      {!error && (
        <div>
          <img src="/calling.gif" width={91} height={87} className="mx-auto" />
          <Text className="text-[#9a9c9e] font-switzer font-[400] text-[12px] text-center mt-2">
            {vapi.callDuration > 0
              ? formatDuration(vapi.callDuration)
              : "Call is connecting..."}
          </Text>
        </div>
      )}

      {error && (
        <Text className="text-red-400 font-switzer font-[400] text-[12px] text-center mt-2">
          Test call failed
        </Text>
      )}

      <Button
        onClick={() => {
          vapi.stopCall();
          setAssistant(null);
          setShouldStartCall(false);
          closeModal();
        }}
        className="!bg-btn font-switzer font-[500] text-[12px] text-background w-[181px] h-[40px]"
      >
        End campaign test
      </Button>
    </div>
  );
}
 */

import React, { useEffect, useState } from "react";
import { Button } from "rizzui/button";
import { Text } from "rizzui/typography";
import useVapi from "../hooks/useVapi";
import { useModal } from "../hooks/useModal";
import { useTestCampaignVariables } from "../hooks/useCampaign";

export default function CampaignTestModal({
  campaign_id,
  fetchAssistantHook = useTestCampaignVariables,
  vapiInstance = null,
  modal = null,
  autoStart = true,
  loadingText = "Fetching assistant...",
  connectingText = "Call is connecting...",
  errorMessage = "Test call failed",
  buttonText = "End campaign test",
  callImage = "/calling.gif",
}) {
  const defaultVapi = useVapi();
  const defaultModal = useModal();

  const vapi = vapiInstance || defaultVapi;
  const { isOpen, closeModal } = modal || defaultModal;

  const [assistant, setAssistant] = useState(null);
  const [shouldStartCall, setShouldStartCall] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data, error, refetch } = fetchAssistantHook({ id: campaign_id });

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      refetch().finally(() => setIsLoading(false));
    }
  }, [isOpen]);

  useEffect(() => {
    if (data?.data) {
      setAssistant(data.data);
      if (autoStart) setShouldStartCall(true);
    }
  }, [data, autoStart]);

  // Effect 1: prepare assistant + intent
  useEffect(() => {
    if (data?.data) {
      setAssistant(data.data);
      if (autoStart) setShouldStartCall(true);
    }
  }, [data, autoStart]);

  // Effect 2: actually start the call
  useEffect(() => {
    if (isOpen && shouldStartCall && assistant) {
      vapi.startCall(assistant);
      setShouldStartCall(false);
    }
  }, [isOpen, shouldStartCall, assistant, vapi]);

  useEffect(() => {
    if (!isOpen) {
      if (vapi.isListening) {
        vapi.stopCall();
      }

      // Only reset if state actually needs to change
      if (shouldStartCall) {
        setShouldStartCall(false);
      }
      if (assistant !== null) {
        setAssistant(null);
      }
    }
  }, [isOpen, vapi, shouldStartCall, assistant]);

  useEffect(() => {
    if (vapi.callEnded) {
      closeModal();
    }
  }, [vapi.callEnded]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div className="border-muted border-[0.5px] h-[266px] w-[312px] bg-background dark:bg-[#2A2C30] p-[32px] rounded-[8px] shadow-lg flex flex-col justify-between items-center">
      {isLoading && (
        <Text className="text-[#9a9c9e] font-switzer font-[400] text-[12px] text-center">
          {loadingText}
        </Text>
      )}

      {!isLoading && !error && (
        <div>
          <img src={callImage} width={91} height={87} className="mx-auto" />
          <Text className="text-[#9a9c9e] font-switzer font-[400] text-[12px] text-center mt-2">
            {vapi.callDuration > 0
              ? formatDuration(vapi.callDuration)
              : connectingText}
          </Text>
        </div>
      )}

      {!isLoading && error && (
        <Text className="text-red-400 font-switzer font-[400] text-[12px] text-center mt-2">
          {errorMessage}
        </Text>
      )}

      <Button
        onClick={() => {
          vapi.stopCall();
          setAssistant(null);
          setShouldStartCall(false);
          closeModal();
        }}
        className="!bg-btn font-switzer font-[500] text-[12px] text-background w-[181px] h-[40px]"
      >
        {buttonText}
      </Button>
    </div>
  );
}
